import { z } from 'zod';

// src/precedence/enums.generated.ts
var CatalogScope = {
  ENTITY: "entity",
  ADDRESS: "address",
  TRANSACTION: "transaction",
  EVENT: "event",
  INTERNAL_TX: "internal_tx"
};
var CatalogLifecycle = {
  VERIFIED: "verified",
  CANDIDATE: "candidate",
  DEPRECATED: "deprecated",
  QUARANTINED: "quarantined"
};
var MemberTier = {
  CORE: "core",
  DERIVED: "derived"
};
var CatalogKind = {
  TAG: "tag",
  LABEL: "label",
  GROUP: "group",
  ENTITY: "entity"
};
var Severity = {
  INFO: "info",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};
var RiskLevel = {
  SAFE: "safe",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  SEVERE: "severe"
};

// src/precedence/tier.ts
var Tier = {
  USER_OVERRIDE: "user_override",
  USER_BASE: "user_base",
  /** Tenant scope, verified (wire value kept as `customer_override`). */
  CUSTOMER_OVERRIDE: "customer_override",
  /** Tenant scope, unverified (wire value kept as `customer_base`). */
  CUSTOMER_BASE: "customer_base",
  /** Org-wide scope, verified — below tenant, above default. */
  ORG_OVERRIDE: "org_override",
  /** Org-wide scope, unverified. */
  ORG_BASE: "org_base",
  DEFAULT: "default",
  ENTITY_INHERITED: "entity_inherited",
  PROVIDER_CANDIDATE: "provider_candidate"
};
var TENANT_OVERRIDE = Tier.CUSTOMER_OVERRIDE;
var TENANT_BASE = Tier.CUSTOMER_BASE;
var TIER_WEIGHTS = {
  [Tier.USER_OVERRIDE]: 70,
  [Tier.USER_BASE]: 60,
  [Tier.CUSTOMER_OVERRIDE]: 50,
  [Tier.CUSTOMER_BASE]: 40,
  // FE-only, in the gap: below tenant, above the system default. A wire weight
  // is never 36/33, because the backend has no org tier to emit one.
  [Tier.ORG_OVERRIDE]: 36,
  [Tier.ORG_BASE]: 33,
  [Tier.DEFAULT]: 30,
  [Tier.ENTITY_INHERITED]: 20,
  [Tier.PROVIDER_CANDIDATE]: 10
};
function tierWeight(tier) {
  return TIER_WEIGHTS[tier] ?? TIER_WEIGHTS[Tier.PROVIDER_CANDIDATE];
}
function compareTiers(a, b) {
  const wa = tierWeight(a);
  const wb = tierWeight(b);
  if (wa < wb) return -1;
  if (wa > wb) return 1;
  return 0;
}
function deriveTier(input) {
  const { hasCustomerScope, isVerified, inheritedFromEntity } = input;
  const isUserScoped = input.isUserScoped ?? false;
  const isOrgScoped = input.isOrgScoped ?? false;
  if (isUserScoped && isVerified) return Tier.USER_OVERRIDE;
  if (isUserScoped) return Tier.USER_BASE;
  if (hasCustomerScope && isVerified) return Tier.CUSTOMER_OVERRIDE;
  if (hasCustomerScope) return Tier.CUSTOMER_BASE;
  if (isOrgScoped && isVerified) return Tier.ORG_OVERRIDE;
  if (isOrgScoped) return Tier.ORG_BASE;
  if (inheritedFromEntity) return Tier.ENTITY_INHERITED;
  if (isVerified) return Tier.DEFAULT;
  return Tier.PROVIDER_CANDIDATE;
}
function mergeByPrecedence(hits, keyFn, tierFn) {
  const chosen = /* @__PURE__ */ new Map();
  for (const hit of hits) {
    const k = keyFn(hit);
    const existing = chosen.get(k);
    if (existing === void 0 || tierWeight(tierFn(hit)) > tierWeight(tierFn(existing))) {
      chosen.set(k, hit);
    }
  }
  return [...chosen.values()];
}
function resolve(hits, opts) {
  const { keyFn, deriveTierFn } = opts;
  const tenantContext = opts.tenantContext ?? null;
  const chainId = opts.chainId ?? null;
  return mergeByPrecedence(hits, keyFn, (h) => deriveTierFn(h, tenantContext, chainId));
}

// src/precedence/overrides.ts
function isSuppressed(lifecycle) {
  return lifecycle === CatalogLifecycle.QUARANTINED;
}
function filterSuppressed(hits, lifecycleFn) {
  return [...hits].filter((hit) => !isSuppressed(lifecycleFn(hit)));
}
function applyOverrides(base, overrides, opts) {
  const all = [...base, ...overrides];
  const lifecycleFn = opts.lifecycleFn;
  const winners = mergeByPrecedence(all, opts.keyFn, opts.tierFn);
  if (!lifecycleFn) return winners;
  return winners.filter((hit) => !isSuppressed(lifecycleFn(hit)));
}

// src/precedence/risk-ladder.generated.json
var risk_ladder_generated_default = {
  bands: {
    high: 60,
    low: 20,
    medium: 40,
    severe: 85
  },
  level_by_severity: {
    critical: "severe",
    high: "high",
    low: "low",
    medium: "medium"
  },
  scale_top: 100,
  score_by_level: {
    high: 72.5,
    low: 30,
    medium: 50,
    safe: 10,
    severe: 92.5
  },
  score_by_severity: {
    critical: 92.5,
    high: 72.5,
    low: 30,
    medium: 50
  },
  severity_by_level: {
    high: "high",
    low: "low",
    medium: "medium",
    severe: "critical"
  }
};

// src/precedence/risk-ladder.ts
var LadderSchema = z.object({
  scale_top: z.number(),
  bands: z.object({
    severe: z.number(),
    high: z.number(),
    medium: z.number(),
    low: z.number()
  }),
  level_by_severity: z.record(z.string(), z.string()),
  severity_by_level: z.record(z.string(), z.string()),
  score_by_level: z.record(z.string(), z.number()),
  score_by_severity: z.record(z.string(), z.number())
});
var SNAPSHOT = LadderSchema.parse(risk_ladder_generated_default);
var RISK_SCORE_SCALE_TOP = SNAPSHOT.scale_top;
var RISK_SCORE_BANDS = Object.freeze({ ...SNAPSHOT.bands });
var LEVEL_BY_SEVERITY = Object.freeze(
  SNAPSHOT.level_by_severity
);
var SEVERITY_BY_LEVEL = Object.freeze(
  SNAPSHOT.severity_by_level
);
var SCORE_BY_SEVERITY = Object.freeze(
  SNAPSHOT.score_by_severity
);
var SCORE_BY_LEVEL = Object.freeze(SNAPSHOT.score_by_level);

// src/precedence/risk.ts
var RISK_LEVEL_ORDER = [
  RiskLevel.SAFE,
  RiskLevel.LOW,
  RiskLevel.MEDIUM,
  RiskLevel.HIGH,
  RiskLevel.SEVERE
];
var RISK_LEVEL_RANK = Object.freeze(
  Object.fromEntries(RISK_LEVEL_ORDER.map((level, i) => [level, i]))
);
var RISK_VALUES = new Set(RISK_LEVEL_ORDER);
function coerceRisk(value) {
  const s = typeof value === "string" ? value.toLowerCase() : "";
  return RISK_VALUES.has(s) ? s : void 0;
}
function worstBy(items, map, levelOf) {
  let worst;
  let worstRank = -1;
  for (const item of items) {
    const mapped = map(item);
    if (mapped === void 0) continue;
    const rank = RISK_LEVEL_RANK[levelOf(mapped)] ?? 0;
    if (rank > worstRank) {
      worst = mapped;
      worstRank = rank;
    }
  }
  return worst;
}
function worstRisk(values) {
  return worstBy(values, coerceRisk, (level) => level);
}
var DEFAULT_RISK_SCORE_BANDS = RISK_SCORE_BANDS;
function riskLevelFromScore(score, bands = DEFAULT_RISK_SCORE_BANDS) {
  if (score == null || !Number.isFinite(score)) return void 0;
  if (score >= bands.severe) return RiskLevel.SEVERE;
  if (score >= bands.high) return RiskLevel.HIGH;
  if (score >= bands.medium) return RiskLevel.MEDIUM;
  if (score >= bands.low) return RiskLevel.LOW;
  return RiskLevel.SAFE;
}

// src/precedence/risk-rules.generated.json
var risk_rules_generated_default = {
  policies: [
    {
      description: "A counterparty is a sanctioned entity or carries a sanctions tag.",
      enabled: true,
      id: "risk:ofac_sanctions",
      label: "OFAC sanctions exposure",
      max_score: 100
    },
    {
      description: "A counterparty is a mixing service, or the transaction itself is a mixing round.",
      enabled: true,
      id: "risk:mixer_exposure",
      label: "Mixer / tumbler exposure",
      max_score: 80
    },
    {
      description: "The sender carries the behavioural dust-spammer tag.",
      enabled: true,
      id: "risk:dust_spammer",
      label: "Dust spammer counterparty",
      max_score: 30
    },
    {
      description: "The sender carries the address-poisoning tag \u2014 an explorer's public name tag, or the platform's own poisoning check (a lookalike seen planting bait in its victims' histories).",
      enabled: true,
      id: "risk:address_poisoning",
      label: "Address poisoning",
      max_score: 75
    },
    {
      description: "The transaction moves a token that claims to be a well-known asset it is not.",
      enabled: true,
      id: "risk:counterfeit_token",
      label: "Counterfeit token",
      max_score: 75
    },
    {
      description: "The transaction moved a large USD value.",
      enabled: true,
      id: "risk:high_value",
      label: "High-value transaction",
      max_score: 60
    },
    {
      description: "Someone other than the account submitted its EIP-7702 delegation, and the transaction then took the account's assets or holds an authorization valid on every chain.",
      enabled: true,
      id: "risk:delegation_phishing",
      label: "EIP-7702 delegation phishing",
      max_score: 75
    },
    {
      description: "An address in the transaction carries a tag an address-forensics check mints \u2014 a drainer, a mixer-funded wallet, a spam token \u2014 scored by the severity the check catalogue gives it.",
      enabled: true,
      id: "risk:forensic_findings",
      label: "Address-forensics findings",
      max_score: 100
    }
  ],
  signals: [
    {
      description: "A counterparty belongs to an entity flagged as sanctioned (e.g. the OFAC SDN list).",
      enabled: true,
      id: "ofac:sanctioned_entity",
      label: "Sanctioned entity",
      policy_id: "risk:ofac_sanctions",
      severity: "critical",
      weight: 100
    },
    {
      description: "A counterparty carries a sanctions tag.",
      enabled: true,
      id: "ofac:sanctioned_tag",
      label: "Sanctions tag",
      policy_id: "risk:ofac_sanctions",
      severity: "critical",
      weight: 100
    },
    {
      description: "A counterparty is tagged as a mixing service (Tornado Cash, a tumbler).",
      enabled: true,
      id: "mixer:tagged_address",
      label: "Mixer-tagged address",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "A counterparty belongs to an entity categorised as a mixer or tumbler.",
      enabled: true,
      id: "mixer:entity_match",
      label: "Mixer entity",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "The transaction itself is a mixing protocol whose implementation is identified \u2014 a Wasabi, Whirlpool or JoinMarket round, a Tornado deposit.",
      enabled: true,
      id: "mixer:protocol",
      label: "Named mixing protocol",
      policy_id: "risk:mixer_exposure",
      severity: "high",
      weight: 80
    },
    {
      description: "The transaction has a CoinJoin's structure but no known implementation built it. Scored below a named mixer: the behaviour is proven, the service is not.",
      enabled: true,
      id: "mixer:structure",
      label: "CoinJoin structure, tool unidentified",
      policy_id: "risk:mixer_exposure",
      severity: "medium",
      weight: 50
    },
    {
      description: "The sender carries the behavioural dust-spammer tag.",
      enabled: true,
      id: "dust:spammer_sender",
      label: "Dust-spammer sender",
      policy_id: "risk:dust_spammer",
      severity: "low",
      weight: 25
    },
    {
      description: "The sender carries the address-poisoning tag \u2014 an explorer's public name tag, or the platform's own poisoning check (a lookalike seen planting bait in its victims' histories).",
      enabled: true,
      id: "poisoning:tagged_sender",
      label: "Address-poisoner sender",
      policy_id: "risk:address_poisoning",
      severity: "high",
      weight: 75
    },
    {
      description: "A token in the transaction calls itself by a registered asset's symbol or name (USDT, USDC, ETH) at an address that is not that asset's on this chain.",
      enabled: true,
      id: "counterfeit:token_claim",
      label: "Counterfeit token",
      policy_id: "risk:counterfeit_token",
      severity: "high",
      weight: 75
    },
    {
      description: "A token in the transaction carries the counterfeit-token catalog tag \u2014 written by the counterfeit-token check when the pipeline resolved it, or by research \u2014 so it is caught even where this transaction's own token facts were not resolved.",
      enabled: true,
      id: "counterfeit:catalog_tag",
      label: "Catalogued counterfeit token",
      policy_id: "risk:counterfeit_token",
      severity: "high",
      weight: 75
    },
    {
      description: "The transaction moved at least $1,000,000.",
      enabled: true,
      id: "high_value:gte_1m",
      label: "At least $1M moved",
      policy_id: "risk:high_value",
      severity: "high",
      weight: 60
    },
    {
      description: "The transaction moved at least $100,000.",
      enabled: true,
      id: "high_value:gte_100k",
      label: "At least $100k moved",
      policy_id: "risk:high_value",
      severity: "medium",
      weight: 40
    },
    {
      description: "Someone other than the account submitted its EIP-7702 delegation, and in the same transaction the account's assets went to that submitter, or were approved for it to spend \u2014 a phished authorization turned into a drained account. A relayer that takes its fee from the account in the same transaction matches too.",
      enabled: true,
      id: "delegation:sponsored_sweep",
      label: "Delegated by another account, then swept to it",
      policy_id: "risk:delegation_phishing",
      severity: "high",
      weight: 75
    },
    {
      description: "Someone other than the account submitted an EIP-7702 authorization the account signed for chain id 0. Whoever holds it can install the same code on the account on every EVM chain; wallets refuse to sign one for that reason.",
      enabled: true,
      id: "delegation:sponsored_any_chain",
      label: "Delegation valid on every chain, submitted by another account",
      policy_id: "risk:delegation_phishing",
      severity: "medium",
      weight: 50
    },
    {
      description: "An address in the transaction carries the funded-by-mixer tag, minted by the funding source check. Who first funded the address \u2014 an exchange withdrawal is a KYC lead; a mixer is a warning.",
      enabled: true,
      id: "forensic:funded-by-mixer",
      label: "Funded from a mixer",
      policy_id: "risk:forensic_findings",
      severity: "medium",
      weight: 50
    },
    {
      description: "An address in the transaction carries the funded-by-sanctioned tag, minted by the funding source check. Who first funded the address \u2014 an exchange withdrawal is a KYC lead; a mixer is a warning.",
      enabled: true,
      id: "forensic:funded-by-sanctioned",
      label: "Funded by a sanctioned address",
      policy_id: "risk:forensic_findings",
      severity: "high",
      weight: 75
    },
    {
      description: "An address in the transaction carries the pass-through tag, minted by the pass-through wallet check. Forwards nearly everything it receives within minutes to wallets that are not exchanges.",
      enabled: true,
      id: "forensic:pass-through",
      label: "Pass-through wallet",
      policy_id: "risk:forensic_findings",
      severity: "low",
      weight: 25
    },
    {
      description: "An address in the transaction carries the mixer-user tag, minted by the protocol, mixer and bridge usage check. Which protocols it used, read from its own transfers against our protocol address books.",
      enabled: true,
      id: "forensic:mixer-user",
      label: "Used a mixer",
      policy_id: "risk:forensic_findings",
      severity: "medium",
      weight: 50
    },
    {
      description: "An address in the transaction carries the spam-token tag, minted by the spam token check. A token pushed onto wallets that never asked for it: airdropped dust, a counterfeit, a provider's spam flag.",
      enabled: true,
      id: "forensic:spam-token",
      label: "Spam token",
      policy_id: "risk:forensic_findings",
      severity: "low",
      weight: 25
    },
    {
      description: "An address in the transaction carries the drainer tag, minted by the drainer check. Pulls tokens out of many unrelated wallets with approvals they granted \u2014 a phishing drainer's shape.",
      enabled: true,
      id: "forensic:drainer",
      label: "Drainer",
      policy_id: "risk:forensic_findings",
      severity: "critical",
      weight: 100
    },
    {
      description: "An address in the transaction carries the airdrop-farmer tag, minted by the airdrop farmer check. Claims airdrops from many distributors and passes them on to one collector.",
      enabled: true,
      id: "forensic:airdrop-farmer",
      label: "Airdrop farmer",
      policy_id: "risk:forensic_findings",
      severity: "low",
      weight: 25
    },
    {
      description: "An address in the transaction carries the scam-deployer tag, minted by the scam deployer check. Deployed contracts the catalog calls counterfeit tokens, spam tokens or drainers.",
      enabled: true,
      id: "forensic:scam-deployer",
      label: "Scam deployer",
      policy_id: "risk:forensic_findings",
      severity: "high",
      weight: 75
    }
  ]
};

// src/precedence/risk-rules.ts
var SIGNALS = new Map(
  risk_rules_generated_default.signals.map((s) => [s.id, s])
);
var POLICIES = new Map(
  risk_rules_generated_default.policies.map((p) => [p.id, p])
);
function riskSignalRule(signalId) {
  return SIGNALS.get(signalId);
}
function riskPolicyRule(policyId) {
  return POLICIES.get(policyId);
}
function riskSignalSettingPath(signalId) {
  return `risk_signals.${signalId}.weight`;
}

export { CatalogKind, CatalogLifecycle, CatalogScope, DEFAULT_RISK_SCORE_BANDS, LEVEL_BY_SEVERITY, MemberTier, RISK_LEVEL_ORDER, RISK_LEVEL_RANK, RISK_SCORE_BANDS, RISK_SCORE_SCALE_TOP, RiskLevel, SCORE_BY_LEVEL, SCORE_BY_SEVERITY, SEVERITY_BY_LEVEL, Severity, TENANT_BASE, TENANT_OVERRIDE, TIER_WEIGHTS, Tier, applyOverrides, coerceRisk, compareTiers, deriveTier, filterSuppressed, isSuppressed, mergeByPrecedence, resolve, riskLevelFromScore, riskPolicyRule, riskSignalRule, riskSignalSettingPath, tierWeight, worstBy, worstRisk };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map